import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Minus, Plus, Trash2, Pencil, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { BottomSheet } from "@/components/BottomSheet";
import { EmptyCart } from "@/components/EmptyStates";
import { UpsellSuggestions } from "@/components/UpsellSuggestions";
import { useUI } from "@/context/UIContext";
import { useCart } from "@/context/CartContext";
import { useOrder } from "@/context/OrderContext";
import { useSession } from "@/context/SessionContext";
import { useLang } from "@/context/LanguageContext";
import { getProductById } from "@/data/menu";
import { getMeta } from "@/data/productMeta";
import { localize, formatTRY } from "@/lib/format";
import { isOnline, restaurantActionsAvailable } from "@/lib/network";
import { guestMenuApiBase, isApiMenuSource } from "@/config/menuSource";
import { submitGuestOrder } from "@/lib/guestOrder";
import { activeGuestEntry, safeSessionStorage } from "@/components/GuestMenuGate";

// Real-order failure -> message key. The cart (and the idempotency key) is kept.
const ORDER_ERROR_KEY = {
  network: "orderNetworkRetry", rate_limited: "orderRateLimited", table_required: "orderTableRequired",
  product_unavailable: "orderItemsUnavailable", note_too_long: "orderNoteTooLong", quantity: "orderQuantityLimit",
  too_many_lines: "orderQuantityLimit", table_session_required: "tableSessionRequired",
};

function selectionSummary(product, selections, lang) {
  if (!product || !selections) return [];
  const labels = [];
  (product.modifierGroups || []).forEach((g) => {
    (selections[g.id] || []).forEach((optId) => {
      const opt = g.options.find((o) => o.id === optId);
      if (opt) labels.push(localize(opt.name, lang));
    });
  });
  return labels;
}

export function CartSheet() {
  const { cartOpen, closeCart, openProduct } = useUI();
  const { detailed, subtotal, setQuantity, removeItem, clearCart, count } = useCart();
  const { activeOrder, placeOrder, addToOrder, recordRemoteOrder } = useOrder();
  const [submitting, setSubmitting] = useState(false);
  const apiMode = isApiMenuSource();
  const { table } = useSession();
  const { lang, t, isRTL } = useLang();
  const navigate = useNavigate();

  const cartProductIds = detailed.map((l) => l.productId);
  const upsellIds = [...new Set(detailed.flatMap((l) => getMeta(l.productId).suggestions || []))]
    .filter((id) => !cartProductIds.includes(id))
    .slice(0, 6);

  const handlePlaceOrder = async () => {
    // Never pretend the order reached the restaurant while offline.
    if (!isOnline()) {
      toast.error(t("offlineOrderBlocked"));
      return;
    }
    // Real menu: every submission is a new backend order (server prices, table
    // from the card). Same cart => same idempotency key on retry.
    if (apiMode) {
      if (submitting) return;
      setSubmitting(true);
      let result;
      try {
        result = await submitGuestOrder(
          { ...(activeGuestEntry() ?? { cardCode: null }), lines: detailed },
          { baseUrl: guestMenuApiBase(), storage: safeSessionStorage() }
        );
      } catch {
        result = { ok: false, reason: "unavailable" };
      } finally {
        setSubmitting(false); // never leave the order button stuck
      }
      if (!result.ok) {
        toast.error(t(ORDER_ERROR_KEY[result.reason] || "orderFailed"));
        return;
      }
      const local = recordRemoteOrder(result.order);
      clearCart();
      closeCart();
      navigate(`/confirmation/${local.id}`);
      return;
    }
    if (!restaurantActionsAvailable()) {
      toast.error(t("actionUnavailable"));
      return;
    }
    if (activeOrder) {
      addToOrder({ detailed, subtotal });
      clearCart();
      closeCart();
      toast.success(t("addedToOrder"));
      navigate("/order");
      return;
    }
    const newOrder = placeOrder({ detailed, subtotal });
    clearCart();
    closeCart();
    navigate(`/confirmation/${newOrder.id}`);
  };

  const handleEdit = (line) => {
    closeCart();
    setTimeout(() => openProduct(line.productId, line.lineId), 220);
  };

  return (
    <BottomSheet open={cartOpen} onClose={closeCart} testId="cart-sheet">
      <div className="flex items-center justify-between px-5 pb-3 pt-1">
        <h2 className="font-heading text-xl font-extrabold">{t("cart")}</h2>
        {count > 0 && table?.tableNumber != null && (
          <span className="rounded-full bg-secondary px-3 py-1 text-xs font-semibold text-muted-foreground">
            {t("table")} {table.tableNumber}
          </span>
        )}
      </div>

      {detailed.length === 0 ? (
        <EmptyCart
          onBrowse={() => {
            closeCart();
            navigate("/menu");
          }}
        />
      ) : (
        <>
          <div className="flex-1 overflow-y-auto overscroll-contain px-4 pb-2">
            <AnimatePresence initial={false}>
              {detailed.map((line) => {
                const product = line.product || getProductById(line.productId);
                const mods = selectionSummary(product, line.selections, lang);
                return (
                  <motion.div
                    key={line.lineId}
                    layout
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, x: isRTL ? 40 : -40 }}
                    className="mb-3 flex gap-3 rounded-2xl border border-border bg-card p-3"
                    data-testid={`cart-item-${line.lineId}`}
                  >
                    <div className="h-20 w-20 shrink-0 overflow-hidden rounded-xl bg-muted">
                      <img src={product?.image} alt="" className="h-full w-full object-cover" />
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col">
                      <div className="flex items-start justify-between gap-2">
                        <h4 className="line-clamp-1 font-heading text-sm font-bold">
                          {localize(product?.name, lang)}
                        </h4>
                        <button
                          onClick={() => removeItem(line.lineId)}
                          className="shrink-0 text-muted-foreground transition-colors hover:text-destructive active:scale-90"
                          aria-label={t("remove")}
                          data-testid={`cart-remove-${line.lineId}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      {mods.length > 0 && (
                        <p className="line-clamp-1 text-xs text-muted-foreground">{mods.join(" · ")}</p>
                      )}
                      {line.note && (
                        <p className="line-clamp-1 text-xs italic text-muted-foreground">
                          "{line.note}"
                        </p>
                      )}
                      <div className="mt-auto flex items-center justify-between pt-1.5">
                        <div className="flex items-center gap-1.5">
                          <div className="flex items-center gap-1 rounded-full border border-border">
                            <button
                              onClick={() => setQuantity(line.lineId, line.quantity - 1)}
                              className="grid h-7 w-7 place-items-center rounded-full active:scale-90"
                              aria-label={t("decrease")}
                              data-testid={`cart-dec-${line.lineId}`}
                            >
                              <Minus className="h-3.5 w-3.5" />
                            </button>
                            <span className="w-5 text-center text-sm font-bold">{line.quantity}</span>
                            <button
                              onClick={() => setQuantity(line.lineId, line.quantity + 1)}
                              className="grid h-7 w-7 place-items-center rounded-full active:scale-90"
                              aria-label={t("increase")}
                              data-testid={`cart-inc-${line.lineId}`}
                            >
                              <Plus className="h-3.5 w-3.5" />
                            </button>
                          </div>
                          {!line.isCombo && (
                            <button
                              onClick={() => handleEdit(line)}
                              className="grid h-7 w-7 place-items-center rounded-full border border-border text-muted-foreground active:scale-90"
                              aria-label={t("edit")}
                              data-testid={`cart-edit-${line.lineId}`}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                        <span className="font-heading text-sm font-extrabold">{formatTRY(line.lineTotal)}</span>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>

            {upsellIds.length > 0 && (
              <div className="mt-4 rounded-2xl border border-border bg-secondary/40 p-3">
                <UpsellSuggestions ids={upsellIds} testId="cart-upsell" />
              </div>
            )}
          </div>

          <div className="shrink-0 space-y-3 border-t border-border bg-card p-4 pb-safe">
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>{t("subtotal")}</span>
                <span data-testid="cart-subtotal">{formatTRY(subtotal)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="font-heading text-base font-bold">{t("estTotal")}</span>
                <span className="font-heading text-xl font-extrabold text-primary" data-testid="cart-total">
                  {formatTRY(subtotal)}
                </span>
              </div>
            </div>
            <Button
              onClick={handlePlaceOrder}
              disabled={submitting}
              className="h-14 w-full rounded-full brand-gradient text-base font-bold text-white shadow-soft"
              data-testid="place-order-btn"
            >
              <ShoppingBag className="me-2 h-5 w-5" /> {activeOrder && !apiMode ? t("addToOrder") : t("placeOrder")}
            </Button>
          </div>
        </>
      )}
    </BottomSheet>
  );
}
